import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  UserCircle, Broadcast, ShieldCheck, Eye, SignOut,
  MusicNote, Key, ArrowRight
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import http from "../lib/api";

const ROLE_META = {
  admin: {
    label: "Admin",
    color: "text-[#FF3B30]",
    border: "border-[#8A2E2E]",
    bg: "bg-[#8A2E2E]/10",
    icon: ShieldCheck,
    desc: "Aap ke paas poora control hai — content, users, aur live streams.",
  },
  moderator: {
    label: "Moderator",
    color: "text-[#4A9EFF]",
    border: "border-[#2E5A8A]",
    bg: "bg-[#2E5A8A]/10",
    icon: ShieldCheck,
    desc: "Aap content manage kar sakte hain.",
  },
  host: {
    label: "Host",
    color: "text-[#4AFF91]",
    border: "border-[#2E8A5A]",
    bg: "bg-[#2E8A5A]/10",
    icon: Broadcast,
    desc: "Aap live stream start aur manage kar sakte hain.",
  },
  viewer: {
    label: "Viewer",
    color: "text-[#B79C7B]",
    border: "border-[#2A2A2D]",
    bg: "bg-[#121214]",
    icon: Eye,
    desc: "Aap sab content sun aur dekh sakte hain.",
  },
};

function ChangePasswordForm() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) return toast.error("Naya password match nahi kiya");
    if (form.next.length < 6) return toast.error("Password 6 characters se kam nahi ho sakta");
    setLoading(true);
    try {
      await http.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password change ho gaya");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password change nahi hua");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3 mt-4">
      <input
        type="password" placeholder="Purana password"
        value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })}
        className="w-full h-11 bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 text-ivory text-sm"
      />
      <input
        type="password" placeholder="Naya password"
        value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })}
        className="w-full h-11 bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 text-ivory text-sm"
      />
      <input
        type="password" placeholder="Naya password dobara"
        value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
        className="w-full h-11 bg-[#0A0A0B] border border-[#2A2A2D] focus:border-[#B79C7B] outline-none px-4 text-ivory text-sm"
      />
      <button
        type="submit" disabled={loading}
        className="w-full h-11 bg-[#1A1A1D] border border-[#2A2A2D] hover:border-[#B79C7B] text-sm text-ivory tracking-widest disabled:opacity-50"
      >
        {loading ? "..." : "PASSWORD BADLEIN"}
      </button>
    </form>
  );
}

export default function Dashboard() {
  const { user, logout, isHost, isAdmin } = useAuth();
  const nav = useNavigate();
  const [showPwForm, setShowPwForm] = useState(false);
  const meta = ROLE_META[user?.role] || ROLE_META.viewer;
  const RoleIcon = meta.icon;

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-[#A1A1AA] mb-4">Dashboard dekhne ke liye login karein</p>
          <Link to="/login" className="inline-flex h-11 px-8 items-center bg-[#8A2E2E] hover:bg-[#A33B3B] text-sm tracking-widest">
            LOGIN
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-12 space-y-8">

      {/* Profile card */}
      <div className={`border ${meta.border} ${meta.bg} p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6`}>
        <div className="w-16 h-16 border border-[#2A2A2D] bg-[#0A0A0B] flex items-center justify-center shrink-0">
          <UserCircle size={36} weight="duotone" className={meta.color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`overline ${meta.color} mb-1 flex items-center gap-2`}>
            <RoleIcon size={14} weight="fill" /> {meta.label}
          </div>
          <h1 className="font-serif text-3xl text-ivory">{user.username}</h1>
          <p className="text-sm text-[#A1A1AA] mt-1">{user.email}</p>
          <p className="text-sm text-[#71717A] mt-2">{meta.desc}</p>
        </div>
        <button
          onClick={() => { logout(); nav("/"); }}
          className="flex items-center gap-2 px-4 h-10 border border-[#2A2A2D] hover:border-[#FF3B30] text-sm text-[#A1A1AA] hover:text-[#FF3B30] transition-colors shrink-0"
        >
          <SignOut size={16} weight="duotone" /> Logout
        </button>
      </div>

      {/* Role-specific quick actions */}
      <div className="grid sm:grid-cols-2 gap-4">

        {/* Everyone: Browse content */}
        <Link to="/" className="border border-[#2A2A2D] bg-[#121214] p-6 hover:border-[#B79C7B] transition-colors group">
          <MusicNote size={24} weight="duotone" className="text-[#B79C7B] mb-3" />
          <div className="font-serif text-lg text-ivory">Archive</div>
          <div className="text-sm text-[#A1A1AA] mt-1">Nohay, majalis, manqabat sunein</div>
          <div className={`text-xs ${meta.color} mt-3 flex items-center gap-1 group-hover:gap-2 transition-all`}>
            Khulein <ArrowRight size={12} />
          </div>
        </Link>

        {/* Host / Admin: Go Live */}
        {isHost && (
          <Link to="/live" className="border border-[#2E8A5A] bg-[#2E8A5A]/10 p-6 hover:bg-[#2E8A5A]/20 transition-colors group">
            <Broadcast size={24} weight="duotone" className="text-[#4AFF91] mb-3" />
            <div className="font-serif text-lg text-ivory">Live Room</div>
            <div className="text-sm text-[#A1A1AA] mt-1">Stream start ya manage karein</div>
            <div className="text-xs text-[#4AFF91] mt-3 flex items-center gap-1 group-hover:gap-2 transition-all">
              Live Room Khulein <ArrowRight size={12} />
            </div>
          </Link>
        )}

        {/* Admin: Admin Panel */}
        {isAdmin && (
          <Link to="/admin" className="border border-[#8A2E2E] bg-[#8A2E2E]/10 p-6 hover:bg-[#8A2E2E]/20 transition-colors group">
            <ShieldCheck size={24} weight="duotone" className="text-[#FF3B30] mb-3" />
            <div className="font-serif text-lg text-ivory">Admin Panel</div>
            <div className="text-sm text-[#A1A1AA] mt-1">Users, content, aur streams manage karein</div>
            <div className="text-xs text-[#FF3B30] mt-3 flex items-center gap-1 group-hover:gap-2 transition-all">
              Admin Panel Khulein <ArrowRight size={12} />
            </div>
          </Link>
        )}

        {/* Viewer: upgrade notice */}
        {!isHost && (
          <div className="border border-[#2A2A2D] bg-[#121214] p-6 opacity-60">
            <Broadcast size={24} weight="duotone" className="text-[#71717A] mb-3" />
            <div className="font-serif text-lg text-ivory">Live Host</div>
            <div className="text-sm text-[#A1A1AA] mt-1">Live stream ke liye host role chahiye</div>
            <div className="text-xs text-[#71717A] mt-3">Admin se rabta karein</div>
          </div>
        )}
      </div>

      {/* Change password */}
      <div className="border border-[#2A2A2D] bg-[#121214] p-6">
        <button
          onClick={() => setShowPwForm((v) => !v)}
          className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-ivory transition-colors"
        >
          <Key size={16} weight="duotone" className="text-[#B79C7B]" />
          Password badlein
          <ArrowRight size={12} className={`transition-transform ${showPwForm ? "rotate-90" : ""}`} />
        </button>
        {showPwForm && <ChangePasswordForm />}
      </div>

    </div>
  );
}
